import Events from 'events';
import { SbLoggerI } from '../types';
import { SbMediaStreamType, SbMediaStreams } from '../media-streams';

export enum WebRtcClientEvent {
  connect = 'connect',
  data = 'data',
  disconnect = 'disconnect',
  streamout = 'streamout',
  streamin = 'streamin',
}

export enum VideoEffect {
  lightBlur = 'light_blur',
  strongBlur = 'strong_blur',
  image = 'image',
}

export type WebRtcClientEventHandler = {
  [WebRtcClientEvent.connect]: () => void;
  [WebRtcClientEvent.data]: (buffer: string) => void;
  [WebRtcClientEvent.disconnect]: (code: SbPeerDisconnectCode) => void;
  [WebRtcClientEvent.streamout]: (stream: MediaStream) => void;
  [WebRtcClientEvent.streamin]: (
    stream: null | MediaStream,
    type: SbMediaStreamType,
  ) => void;
};

export enum SbPeerDisconnectCode {
  CLOSED = 'CLOSED',
  ERROR = 'ERROR',
}

export interface WebRtcClientEmitter extends Events.EventEmitter {
  on: <T extends WebRtcClientEvent>(k: T, cb: WebRtcClientEventHandler[T]) => this;
  emit(event: WebRtcClientEvent, ...args: any[]): boolean;
}

export type ConnectOptions = {
  token?: string;
  room?: string;
};

export abstract class WebRtcClient {
  #webRtcEvents = new Events.EventEmitter() as WebRtcClientEmitter;
  protected logger: SbLoggerI;
  protected mediaStreams: SbMediaStreams;

  constructor(mediaStreams: SbMediaStreams, logger: SbLoggerI) {
    this.logger = logger;
    this.mediaStreams = mediaStreams;
  }

  on(event: WebRtcClientEvent, listener: (...args: any[]) => void) {
    this.#webRtcEvents.on(event, listener);
    return this;
  }

  emit(event: WebRtcClientEvent, ...args: any[]): boolean {
    return this.#webRtcEvents.emit(event, ...args);
  }

  removeAllListeners() {
    this.#webRtcEvents.removeAllListeners();
  }

  // @ts-ignore
  static runDiagnostics(token: string, logger: SbLoggerI) {
    logger.warn('runDiagnostics not implemented');
  }

  abstract connect(options: ConnectOptions): void;
  abstract disconnect(): void;
  abstract sendData(data: string): void;
  abstract isDataChannelOpen(): boolean;
  abstract isConnected(): boolean;
  abstract addStream(stream: MediaStream, streamType: SbMediaStreamType): void;
  abstract removeStream(stream: MediaStream): void;
  abstract getIncomingCameraStream(): null | MediaStream;
  abstract getIncomingDisplayStream(): null | MediaStream;
  abstract getOutgoingCameraStream(): null | MediaStream;
  abstract getOutgoingDisplayStream(): null | MediaStream;
  abstract setOutgoingAudioEnabled(isEnabled: boolean): void;
  abstract setOutgoingVideoEnabled(isEnabled: boolean): void;
}

export interface VideoEffectProcessor {
  prepare(effect: null | VideoEffect, backgroundImageId?: string): Promise<void>;
  apply(stream: MediaStream): MediaStream;
  stop(stream: MediaStream): void;
}
